import { useSelector } from "react-redux";
import FolderIcon from "assets/icons/FolderIcon";
import { FileIcon } from "assets/icons/FileIcon";
import TagIcon from "assets/icons/tagIcon";
import UserTagIcon from "assets/icons/UserTagIcon";
import MislabelsIcon from "assets/icons/MislabelsIcon";
import LabelOutliersIcon from "assets/icons/LabelOutliersIcon";
import DuplicateIcon from "assets/icons/DuplicateIcon";
import OutliersIcon from "assets/icons/OutliersIcon";
import QualityIssuesIcon from "assets/icons/QualityIssuesIcon";
import UniquenessIcon from "assets/icons/UniquenessIcon";
import { DateTimeIcon } from "assets/icons/CustomMetadataIcons";
import {
  DatasetStatusEnum,
  EntityType,
  FilterMenuItem,
  Filters,
} from "../types";
import {
  getIsUniquenessFilterEnabled,
  getIsDuplicatesThresholdEnabled,
  getDataset,
  getIssueTypes,
} from "redux/SingleDataset/selectors";
import { getIsAddMediaEnabled } from "../redux/Datasets/selectors";
import { useFeatureFlagService } from "./useFeatureFlagService";
import { NEW_STATUS, UNIQUENESS_SCORE } from "../helpers/constants/filters";

interface UseFilterConfigParams {
  entityType: EntityType | null;
}

const RANGE_ISSUES = ["OUTLIERS", "MISLABELS", "DUPLICATES", "LABEL OUTLIER"];

export const useFilterConfig = ({
  entityType,
}: UseFilterConfigParams): FilterMenuItem[] => {
  const isUniquenessEnabled = useSelector(getIsUniquenessFilterEnabled);
  const isDuplicatesThresholdEnabled = useSelector(
    getIsDuplicatesThresholdEnabled,
  );
  const isAddMediaEnabled = useSelector(getIsAddMediaEnabled);
  const dataset = useSelector(getDataset);
  const issues = useSelector(getIssueTypes) || [];

  const datasetStatus = dataset?.status as DatasetStatusEnum;
  const isUpdating =
    datasetStatus === "UPDATING" || datasetStatus === "PENDING_INDEX";

  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;
  const disabledUniqueness = !useFeatureFlagService(UNIQUENESS_SCORE).enabled;

  const availableIssues = new Set(
    issues.map((i) => i.text.toUpperCase().replace(/\s+/g, "_")),
  );

  const hasNonRangeIssues = issues.some(
    (i) => !RANGE_ISSUES.includes(i.text.toUpperCase()),
  );

  // --- Static Filters ---
  const filterConfig: FilterMenuItem[] = [
    {
      icon: <FolderIcon />,
      type: Filters.FOLDER,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <FileIcon />,
      type: Filters.FILE,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <TagIcon />,
      type: Filters.LABELS,
      entityTypes: ["IMAGES", "OBJECTS"],
      disabled: false,
      hidden: false,
    },
    {
      icon: <UserTagIcon />,
      type: Filters.USER_TAGS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <MislabelsIcon />,
      type: Filters.MISLABELS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.MISLABELS),
    },
    {
      icon: <LabelOutliersIcon />,
      type: Filters.LABEL_OUTLIER,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.LABEL_OUTLIER),
    },
    {
      icon: <DuplicateIcon />,
      type: Filters.DUPLICATES,
      entityTypes: ["IMAGES", "OBJECTS"],
      disabled: !isDuplicatesThresholdEnabled,
      hidden: !availableIssues.has(Filters.DUPLICATES),
    },
    {
      icon: <OutliersIcon />,
      type: Filters.OUTLIERS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.OUTLIERS),
    },
    {
      icon: <QualityIssuesIcon />,
      type: Filters.ISSUES,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !hasNonRangeIssues,
    },
    {
      icon: <UniquenessIcon />,
      type: Filters.UNIQUENESS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !isUniquenessEnabled,
      disabled: displayNewStatus ? disabledUniqueness : isUpdating,
    },
    {
      icon: <DateTimeIcon />,
      type: Filters.CREATED_AT,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !isAddMediaEnabled,
    },
    {
      icon: <TagIcon />,
      type: Filters.MEDIA_STATUS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !isAddMediaEnabled,
    },
  ];

  // --- Combine and filter ---
  return [...filterConfig].filter(
    (item) =>
      !item.hidden &&
      (!item.entityTypes ||
        entityType === null ||
        item.entityTypes.includes(entityType)),
  );
};
